'use client';

import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { useCallback, useMemo, useState } from 'react';
import type { TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useParams } from 'next/navigation';
import { useSnackbar } from 'notistack';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import {
  FilterInput,
  LogicalOperator,
  type FilterGroup,
} from '@/components/publicSearch/constants';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { useSearchType } from './useSearchType';
import {
  pathForSearchType,
  type SearchType as PublicSearchType,
} from '@/lib/publicSearchPaths';
import {
  CREATE_SHARED_SEARCH,
  CreateSharedSearchResponse,
  CreateSharedSearchVariables,
} from '@/publicSearch/graphql/sharedSearch';

export interface ShareFilterInput {
  searchType: string;
  filters: FilterInput[];
  commonFilterOperator: string;
  searchTerm: string;
  gridSortModel: GridSortModel;
  isAiMode: boolean;
  filterGroups: FilterGroup[];
  groupsOperator: string;
}

export function useShareSearch() {
  const searchType = useSearchType();
  const { filters, commonFilterOperator, filterGroups, groupsOperator } =
    useFilterStore(searchType);
  const { basicSearchInput, isAiMode } = useSearchStore();
  const gridSortModel = useGridStore((s) => s.gridSortModel);
  const orgId = usePublicSearchContext((ctx) => ctx.society.orgId);
  const params = useParams();
  const orgName = params?.orgName as string;
  const { enqueueSnackbar } = useSnackbar();
  const [isSharing, setIsSharing] = useState(false);

  const requestContext = useMemo(
    () => ({ headers: setDefaultHeader(orgId) }),
    [orgId],
  );

  const [createSharedSearch] = useMutation(
    CREATE_SHARED_SEARCH as TypedDocumentNode<
      CreateSharedSearchResponse,
      CreateSharedSearchVariables
    >,
  );

  const hasGroupedFilters = filterGroups.some((g) => g.filters.length > 0);
  const hasActiveFilters =
    filters.length > 0 ||
    (basicSearchInput && basicSearchInput.trim() !== '') ||
    hasGroupedFilters ||
    gridSortModel.length > 0;

  const groupsOpString =
    groupsOperator === LogicalOperator.or
      ? LogicalOperator.or
      : LogicalOperator.and;

  const createAndCopyLink = useCallback(
    async (input: ShareFilterInput) => {
      if (!orgId) return;

      setIsSharing(true);
      try {
        const response = await createSharedSearch({
          variables: {
            societyId: Number(orgId),
            input: {
              searchType: input.searchType,
              filters: input.filters.map((f) => ({
                field: f.field,
                operator: f.operator,
                value: f.value,
              })),
              commonFilterOperator: input.commonFilterOperator,
              searchTerm: input.searchTerm,
              gridSortModel: input.gridSortModel.map((s) => ({
                field: s.field,
                sort: s.sort ?? null,
              })),
              isAiMode: input.isAiMode,
              filterGroups: input.filterGroups.map((g) => ({
                logicalOperator:
                  g.logicalOperator === LogicalOperator.or
                    ? LogicalOperator.or
                    : LogicalOperator.and,
                filters: g.filters.map((f) => ({
                  field: f.field,
                  operator: f.operator,
                  value: f.value,
                })),
              })),
              groupsOperator: input.groupsOperator,
              aiSearchPrompt: '',
            },
          },
          context: requestContext,
        });

        const shortCode = response.data?.createSharedSearch?.shortCode;
        if (!shortCode) {
          enqueueSnackbar('Failed to create shareable link', {
            variant: SNACKBAR_VARIANT.ERROR,
            autoHideDuration: 3000,
          });
          return;
        }

        const { pathname, query } = pathForSearchType(
          orgName,
          input.searchType as PublicSearchType,
          null,
        );
        query.set('shared', shortCode);
        const url = new URL(
          `${pathname}${query.toString() ? `?${query.toString()}` : ''}`,
          window.location.origin,
        );

        await navigator.clipboard.writeText(url.toString());
        enqueueSnackbar('Search link copied to clipboard', {
          variant: SNACKBAR_VARIANT.SUCCESS,
          autoHideDuration: 2000,
        });
      } catch {
        enqueueSnackbar('Failed to create shareable link', {
          variant: SNACKBAR_VARIANT.ERROR,
          autoHideDuration: 3000,
        });
      } finally {
        setIsSharing(false);
      }
    },
    [orgId, createSharedSearch, requestContext, orgName, enqueueSnackbar],
  );

  const shareSearch = useCallback(async () => {
    if (!hasActiveFilters) return;
    const hasGrouped = filterGroups.some((g) => g.filters.length > 0);
    await createAndCopyLink({
      searchType,
      filters,
      commonFilterOperator,
      searchTerm: hasGrouped ? '' : basicSearchInput || '',
      gridSortModel,
      isAiMode: hasGrouped ? false : isAiMode,
      filterGroups,
      groupsOperator: groupsOpString,
    });
  }, [
    hasActiveFilters,
    createAndCopyLink,
    searchType,
    filters,
    commonFilterOperator,
    basicSearchInput,
    gridSortModel,
    isAiMode,
    filterGroups,
    groupsOpString,
  ]);

  return {
    shareSearch,
    shareSavedFilter: createAndCopyLink,
    isSharing,
    hasActiveFilters: hasActiveFilters,
  };
}
